import { useFlags } from 'launchdarkly-react-client-sdk';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { Button, Card, LinkOutIcon } from '@wonderschool/common-base-ui';
import PageTitle from '../../Components/Shared/PageTitle';
import { getTitlePrefix } from '../../config/env';

import { RouteNameEnum, useRoutes } from '../../navigation';
import { shouldHideOpenLedger } from '../../navigation/navigationUtils';

// Import assets using relative paths instead of importing as components
import { ReactComponent as BizCheckIcon } from './assets/biz-check.svg';
import { ReactComponent as KinderSystemsIcon } from './assets/kinder-systems.svg';
import LearningBeyondPaperIcon from './assets/learning-beyond-paper-logo.png';
import { ReactComponent as OpenLedgerIcon } from './assets/open-ledger-logo.svg';
import { REDIRECTION_LINKS } from './enums';
import { addUserParamsToUrl } from './utils';

const AppStore = () => {
  const { t } = useTranslation();
  const { gotoRouteByName } = useRoutes();
  const title = t('apps.title');

  const { isProviderIntakeVisibleAppStore } = useFlags();

  const items = [
    {
      id: 'learning-beyond-paper',
      title: 'Learning Beyond Paper',
      subTitle: '',
      pricing: '',
      description: t('apps.learningBeyondPaperDescription'),
      icon: LearningBeyondPaperIcon,
      tags: [{ title: t('apps.tags.lessonPlanning'), color: 'purple' }],
      button: {
        id: 'learning-beyond-paper-join-waitlist',
        label: t('apps.joinWaitlist'),
        link: addUserParamsToUrl(REDIRECTION_LINKS?.LEARNING_BEYOND_PAPER_GOOGLE_FORM),
        showPostIcon: true,
      },
      learnMore: {
        showButton: true,
        title: t('apps.learnMore'),
        link: RouteNameEnum.LEARNING_BEYOND_PAPER,
      },
      enabled: true,
    },
    {
      id: 'business-intake',
      title: 'Childcare Business Health Check',
      description: t('apps.healthCheckDescription'),
      ctaLabel: t('common.getStarted'),
      button: {
        id: 'business-intake-learn-more',
        label: t('common.getStarted'),
        link: addUserParamsToUrl(REDIRECTION_LINKS?.CHILDCARE_BUSINESS_HEALTH_CHECK),
        showPostIcon: true,
      },
      icon: BizCheckIcon,
      tags: [{ title: t('apps.tags.businessSustainability'), color: 'orange' }],
      externalLink: REDIRECTION_LINKS?.CHILDCARE_BUSINESS_HEALTH_CHECK || '',
      enabled: isProviderIntakeVisibleAppStore,
    },
    {
      id: 'kinder-systems',
      title: 'KinderConnect',
      description: t('apps.kinderConnect.description'),
      ctaLabel: t('apps.kinderConnect.cta'),
      button: {
        id: 'kinder-systems-learn-more',
        link: RouteNameEnum.KINDER_CONNECT,
        internalRoute: true,
        showPostIcon: false,
      },
      icon: KinderSystemsIcon,
      tags: [{ title: t('apps.tags.subsidyManagement'), color: 'blue' }],
      enabled: true,
    },
    {
      id: 'open-ledger',
      title: 'OpenLedger',
      description: t('apps.openLedger.description'),
      ctaLabel: t('apps.openLedger.cta'),
      button: {
        id: 'open-ledger-learn-more',
        label: t('apps.openLedger.cta'),
        link: RouteNameEnum.OPEN_LEDGER,
        internalRoute: true,
        showPostIcon: false,
      },
      icon: OpenLedgerIcon,
      tags: [{ title: t('apps.tags.accounting'), color: 'red' }],
      enabled: !shouldHideOpenLedger(),
    },
  ];

  const renderPageHead = () => (
    <Helmet>
      <title>
        {getTitlePrefix()}
        {t('pages.appStore.browserTitle')}
      </title>
    </Helmet>
  );

  return (
    <div className="container mx-auto px-5">
      {renderPageHead()}
      <PageTitle title={t('apps.title')} />
      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {items.map(
          (item, index) =>
            item.enabled && (
              <Card key={index}>
                <div className="relative min-h-60 px-4 py-5 sm:px-6">
                  {item?.tags.map((tag, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center rounded-full bg-${tag.color}-200 px-3 py-0.5 text-xs font-semibold text-${tag.color}-950`}
                    >
                      {tag.title.toUpperCase()}
                    </span>
                  ))}
                  <div className="mt-4 flex space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-gray-300 object-contain">
                      {typeof item?.icon === 'string' ? (
                        <img className="mx-auto flex-shrink-0 rounded-full" src={item?.icon} alt={item?.title} />
                      ) : (
                        <item.icon className="mx-auto h-full w-full flex-shrink-0 p-1" />
                      )}
                    </div>
                    <div className="m-auto text-2xl font-bold text-gray-800">{item?.title}</div>
                  </div>
                  {item?.subTitle && (
                    <div className="mt-2 text-sm font-bold text-gray-800">
                      {item.subTitle}
                      {item?.pricing && <span className="font-normal"> {item.pricing}</span>}
                    </div>
                  )}
                  {item?.description && <p className="mt-2 text-sm text-gray-800">{item.description}</p>}
                  <div className="mt-6">
                    <Button
                      label={item?.ctaLabel || t('apps.joinWaitlist')}
                      id={item?.button?.id}
                      primary
                      postIcon={item?.button?.showPostIcon ? <LinkOutIcon className="h-5 w-5" /> : undefined}
                      onClick={() => {
                        if (item?.button?.link) {
                          if (item?.button?.internalRoute) {
                            gotoRouteByName(item.button.link);
                          } else {
                            window.open(item.button.link, '_blank');
                          }
                        }
                      }}
                    />
                    {item.learnMore && (
                      <button
                        type="button"
                        className="mx-4 cursor-pointer border-0 bg-transparent p-0 font-bold text-blue-900 hover:underline"
                        onClick={() => item.learnMore.link && gotoRouteByName(item.learnMore.link)}
                      >
                        {item?.learnMore?.title || t('apps.learnMore')}
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            )
        )}
        {/* This div is for HotJar Survey */}
        <div className="rounded-lg p-4" id="appstore-page-hotjar"></div>
        {/* This div is for HotJar Survey */}
      </div>
    </div>
  );
};

export default AppStore;
